import { ReactNode } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import logoMgCode from 'assets/images/brand/logoMGCodeSideNavDarkMode.png';
import { ChannelFlowCanvas } from './channel-flow-canvas';

/// Largura da coluna por faixa; deve bater com gridTemplateColumns (de onde sai o centro do vórtice).
const COLUMN_MD = 420;
const COLUMN_LG = 480;

/// O cartão flutua sobre o preto em qualquer tema: os campos não herdam as cores do modo claro.
export const AUTH_FIELD_SX = {
    '& .MuiOutlinedInput-root': {
        color: '#fff',
        '& fieldset': { borderColor: 'rgba(255,255,255,0.22)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
    '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.55)' },
};

interface Props {
    /// O texto de apoio embaixo da marca.
    subtitle: string;
    onSubmit?: (e: React.FormEvent) => void;
    children: ReactNode;
}

/// Moldura das telas sem sessão: funil de canais ao fundo e coluna de vidro com a marca.
export const AuthLayout = ({ subtitle, onSubmit, children }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const aboveMd = useMediaQuery(theme.breakpoints.up('md'));
    const aboveLg = useMediaQuery(theme.breakpoints.up('lg'));

    // Abaixo de md a coluna ocupa a tela toda e não há área livre: o vórtice volta ao centro da tela.
    const formColumn = aboveLg ? COLUMN_LG : aboveMd ? COLUMN_MD : 0;

    return (
        <Box sx={{ position: 'relative', minHeight: '100vh', background: '#060610', overflow: 'hidden' }}>
            {/* O funil é o fundo da tela inteira, não de um painel. */}
            <ChannelFlowCanvas rightInset={formColumn} />

            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    minHeight: '100vh',
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: `1fr ${COLUMN_MD}px`, lg: `1fr ${COLUMN_LG}px` },
                    alignItems: 'center',
                }}
            >
                {/* O texto não intercepta o cursor: o mouse comanda as partículas no canvas. */}
                <Box
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        alignSelf: 'stretch',
                        p: 6,
                        pointerEvents: 'none',
                    }}
                >
                    <Box sx={{ maxWidth: 460 }}>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '2.1rem', lineHeight: 1.2, mb: 1.5 }}>
                            {t('auth.painelTitulo')}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontSize: '1rem' }}>
                            {t('auth.painelSubtitulo')}
                        </Typography>
                    </Box>
                </Box>

                {/* O vidro é a coluna inteira: divide a tela sem virar painel opaco. */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        alignSelf: 'stretch',
                        p: { xs: 3, sm: 5 },
                        background: 'rgba(255,255,255,0.04)',
                        backdropFilter: 'blur(18px)',
                        WebkitBackdropFilter: 'blur(18px)',
                        borderLeft: { md: '1px solid rgba(255,255,255,0.12)' },
                        boxShadow: { md: '-24px 0 64px rgba(0,0,0,0.45)' },
                    }}
                >
                    <Box
                        component="form"
                        onSubmit={onSubmit ?? ((e: React.FormEvent) => e.preventDefault())}
                        sx={{ width: '100%', maxWidth: 380, display: 'grid', gap: 2.2 }}
                    >
                        {/* A marca no lugar do título: o botão já diz a ação. */}
                        <Box sx={{ textAlign: 'center' }}>
                            <Box component="img" src={logoMgCode} alt="MGCODE" sx={{ width: 300, display: 'block', mx: 'auto' }} />
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                {subtitle}
                            </Typography>
                        </Box>

                        {children}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
